import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Autocomplete,
  Checkbox,
  Tooltip,
} from '@mui/material';
import {
  AddRounded,
  EditOutlined,
  DeleteOutline,
  CheckRounded,
  SchoolRounded,
  GroupsRounded,
  LockOpenRounded,
  InfoOutlined,
  SchoolOutlined,
  LockOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DECEASED_IN_SOURCE_TAG, keepSourceTags } from '@dosfilos/domain';
import { useAuth } from '../../../contexts/auth.context';
import { errorText } from '../../../lib/error-text';
import { callable } from '../../../lib/callable';

// ─── Color palette por tipo ────────────────────────────────────────────────

export const TYPE_COLORS: Record<
  string,
  'primary' | 'success' | 'warning' | 'info' | 'default' | 'error'
> = {
  // Ciclo de vida académico
  student: 'primary',
  alumni: 'info',
  'prospecto-académico': 'warning',
  externo: 'error',
  // Roles institucionales
  faculty: 'success',
  docente: 'success',
  tutor: 'success',
  decano: 'success',
  academico: 'success',
  coordinador: 'success',
  capellan: 'success',
  staff: 'warning',
  contact: 'default',
  // Roles de sistema como PersonType (backward compat display)
  tenantAdmin: 'error',
  registrar: 'warning',
  reviewer: 'info',
  mentor: 'success',
  finance: 'success',
  donor: 'default',
};

// ─── Catálogo de cargos institucionales ───────────────────────────────────

export const INSTITUTIONAL_ROLES: Array<{ value: string; label: string; description: string }> = [
  { value: 'docente', label: 'Docente', description: 'Imparte cursos y asignaturas' },
  { value: 'tutor', label: 'Tutor', description: 'Acompañamiento académico o pastoral' },
  { value: 'decano', label: 'Decano', description: 'Dirige una facultad o departamento' },
  { value: 'academico', label: 'Académico', description: 'Coordinación académica institucional' },
  { value: 'coordinador', label: 'Coordinador', description: 'Coordina programas o áreas' },
  { value: 'capellan', label: 'Capellán', description: 'Guía espiritual y pastoral' },
  { value: 'staff', label: 'Staff', description: 'Personal administrativo general' },
];

// ─── Ciclo de vida académico ───────────────────────────────────────────────

const LIFECYCLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'externo', label: 'Externo' },
  { value: 'prospecto-académico', label: 'Prospecto' },
  { value: 'student', label: 'Estudiante' },
  { value: 'alumni', label: 'Egresado (Alumni)' },
];

// ─── RoleToggleChip — toggle chip reutilizable ────────────────────────────

function RoleToggleChip({
  label,
  description,
  selected,
  onToggle,
  color = 'primary',
}: {
  label: string;
  description?: string;
  selected: boolean;
  onToggle: () => void;
  color?: string;
}) {
  return (
    <Box
      onClick={onToggle}
      sx={{
        border: '1.5px solid',
        borderColor: selected ? `${color}.main` : 'divider',
        borderRadius: 2,
        p: 1.25,
        cursor: 'pointer',
        bgcolor: selected ? `${color}.dark` : 'action.hover',
        transition: 'all 0.15s ease',
        position: 'relative',
        '&:hover': {
          borderColor: `${color}.main`,
          bgcolor: selected ? `${color}.dark` : `${color}.dark`,
          '& .hover-bg': { opacity: 0.06 },
        },
      }}
    >
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckRounded sx={{ fontSize: 10, color: '#fff' }} />
        </Box>
      )}
      <Typography
        variant="body2"
        fontWeight={700}
        color={selected ? `${color}.light` : 'text.primary'}
        lineHeight={1.2}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={0.25}
          lineHeight={1.3}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────

function SectionHeader({
  icon,
  title,
  subtitle,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}) {
  return (
    <Stack direction="row" alignItems="flex-start" gap={1} mb={1.5}>
      <Box sx={{ color: 'text.secondary', display: 'flex', mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="body2" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
    </Stack>
  );
}

// ─── PersonData ─────────────────────────────────────────────────────────────

export interface PersonData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  types: string[];
  institutionalRoles?: string[];
  tags: string[];
  organizationName?: string;
}

// ─── Create Person Dialog ──────────────────────────────────────────────────

export function CreatePersonDialog({
  open,
  onClose,
  tenantId,
  defaultTypes = ['student'],
  institutionalOnly = false,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string | null;
  defaultTypes?: string[];
  institutionalOnly?: boolean;
}) {
  const { t } = useTranslation('sis');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organizationName: '',
    lifecycleStage: institutionalOnly
      ? ''
      : (defaultTypes.find((d) =>
          ['externo', 'prospecto-académico', 'student', 'alumni'].includes(d),
        ) ?? 'student'),
    institutionalRoles: institutionalOnly
      ? defaultTypes.filter((d) => INSTITUTIONAL_ROLES.some((r) => r.value === d))
      : ([] as string[]),
    tags: [] as string[],
  });
  const [grantAccess, setGrantAccess] = useState(false);
  const [sendInvite, setSendInvite] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStep, setSaveStep] = useState('');
  const [error, setError] = useState('');

  const isStudent = form.lifecycleStage === 'student';
  const hasInstitutionalRole = form.institutionalRoles.length > 0;
  const showAccessControl = !isStudent && (institutionalOnly || hasInstitutionalRole);

  // Reset access switch when student lifecycle is selected
  function handleLifecycleChange(value: string) {
    setForm((f) => ({ ...f, lifecycleStage: f.lifecycleStage === value ? '' : value }));
    if (value === 'student') setGrantAccess(false);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantId) {
      setError('Error interno: No se ha seleccionado una organización.');
      return;
    }

    const types: string[] = [];
    if (form.lifecycleStage) types.push(form.lifecycleStage);

    setSaving(true);
    setError('');
    setSaveStep('Creando persona...');

    let personId: string | null = null;
    try {
      const createPerson = callable<object, { id: string }>('createPerson');
      const result = await createPerson({
        tenantId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        organizationName: form.organizationName,
        types,
        institutionalRoles: form.institutionalRoles,
        tags: form.tags,
      });
      personId = result.data.id;
    } catch (err: any) {
      setError(err?.message || 'Ocurrió un error al crear la persona');
      setSaving(false);
      setSaveStep('');
      return;
    }

    if (grantAccess && personId) {
      setSaveStep('Configurando acceso al sistema...');
      try {
        const provision = callable('provisionPersonAccess');
        await provision({
          tenantId,
          personId,
          institutionalRoles: form.institutionalRoles,
          sendInvite,
        });
      } catch (err: any) {
        // Partial success: person was created, but access failed.
        setError(
          `Persona creada, pero hubo un error al configurar el acceso: ${err?.message ?? 'Error desconocido'}. Puedes intentarlo nuevamente desde el perfil de la persona.`,
        );
        setSaving(false);
        setSaveStep('');
        return;
      }
    }

    setForm({
      firstName: '',
      lastName: '',
      email: '',
      organizationName: '',
      lifecycleStage: 'student',
      institutionalRoles: [],
      tags: [],
    });
    setGrantAccess(false);
    setSendInvite(true);
    setSaving(false);
    setSaveStep('');
    onClose();
  }

  function toggleInstitutional(role: string) {
    setForm((f) => ({
      ...f,
      institutionalRoles: f.institutionalRoles.includes(role)
        ? f.institutionalRoles.filter((r) => r !== role)
        : [...f.institutionalRoles, role],
    }));
    // If user picks an institutional role, auto-clear student lifecycle
    if (form.lifecycleStage === 'student') setForm((f) => ({ ...f, lifecycleStage: '' }));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <AddRounded />
          Nueva Persona
        </Stack>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            {/* Nombre */}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Nombre"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Apellido"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Correo electrónico"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Organización / Iglesia"
              value={form.organizationName}
              onChange={(e) => setForm((f) => ({ ...f, organizationName: e.target.value }))}
              fullWidth
            />

            <Divider />

            {/* Ciclo de vida académico */}
            {!institutionalOnly && (
              <Box>
                <SectionHeader
                  icon={<SchoolRounded fontSize="small" />}
                  title="Ciclo académico"
                  subtitle="¿En qué etapa está esta persona?"
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                  {LIFECYCLE_OPTIONS.map((opt) => (
                    <RoleToggleChip
                      key={opt.value}
                      label={t(`people.types.${opt.value}`, { defaultValue: opt.label })}
                      selected={form.lifecycleStage === opt.value}
                      onToggle={() => handleLifecycleChange(opt.value)}
                      color="primary"
                    />
                  ))}
                </Box>

                {/* Redirect hint when student chosen */}
                {isStudent && (
                  <Alert
                    severity="info"
                    icon={<SchoolOutlined fontSize="small" />}
                    sx={{ mt: 1.5, borderRadius: 2, fontSize: 13 }}
                  >
                    Para inscribir alumnos con admisión y cuenta de acceso usa el botón{' '}
                    <strong>" + Inscribir Alumno"</strong> en la barra superior.
                  </Alert>
                )}
              </Box>
            )}

            {/* Cargo institucional */}
            <Box>
              <SectionHeader
                icon={<GroupsRounded fontSize="small" />}
                title="Cargo institucional"
                subtitle="¿Qué función desempeña en la institución? (puede tener varios)"
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {INSTITUTIONAL_ROLES.map((role) => (
                  <RoleToggleChip
                    key={role.value}
                    label={t(`people.types.${role.value}`, { defaultValue: role.label })}
                    description={role.description}
                    selected={form.institutionalRoles.includes(role.value)}
                    onToggle={() => toggleInstitutional(role.value)}
                    color="success"
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            {/* Etiquetas */}
            <Autocomplete
              multiple
              freeSolo
              // Lo escrito se agrega también al SALIR del campo, no solo con Enter:
              // antes, guardar sin presionar Enter perdía la etiqueta sin aviso.
              autoSelect
              options={[]}
              value={form.tags}
              onChange={(_, newValue) => setForm((f) => ({ ...f, tags: newValue }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                })
              }
              renderInput={({ size: _size, ...params }) => (
                <TextField
                  {...params}
                  InputLabelProps={params.InputLabelProps as any}
                  label={t('people.dialogs.tags')}
                  placeholder={t('people.dialogs.tagsPlaceholderExamples')}
                  helperText={t('people.dialogs.tagsHelp')}
                />
              )}
            />

            {/* ── Acceso al sistema (solo para personal institucional) ── */}
            {showAccessControl && (
              <>
                <Divider />
                <Box
                  sx={{
                    border: '1.5px solid',
                    borderColor: grantAccess ? 'success.main' : 'divider',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: grantAccess ? 'success.dark' : 'action.hover',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={1}>
                      <LockOpenRounded
                        fontSize="small"
                        sx={{ color: grantAccess ? 'success.light' : 'text.secondary' }}
                      />
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={grantAccess ? 'success.light' : 'text.primary'}
                        >
                          Dar acceso al sistema
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Crea cuenta Firebase y acceso al portal
                        </Typography>
                      </Box>
                    </Stack>
                    <Switch
                      checked={grantAccess}
                      onChange={(_, checked) => setGrantAccess(checked)}
                      color="success"
                    />
                  </Stack>

                  {grantAccess && (
                    <Stack mt={1.5} spacing={1}>
                      <Alert
                        severity="success"
                        icon={<InfoOutlined fontSize="small" />}
                        sx={{ borderRadius: 1.5, py: 0.5, fontSize: 12 }}
                      >
                        Se creará cuenta Firebase Auth, Membership con el cargo seleccionado y
                        acceso al portal institucional.
                      </Alert>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={sendInvite}
                            onChange={(_, c) => setSendInvite(c)}
                            color="success"
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            color={sendInvite ? 'success.light' : 'text.secondary'}
                          >
                            Enviar email de invitación con link de activación
                          </Typography>
                        }
                      />
                    </Stack>
                  )}
                </Box>
              </>
            )}

            {saveStep && (
              <Stack direction="row" alignItems="center" gap={1}>
                <CircularProgress size={14} color="inherit" />
                <Typography variant="caption" color="text.secondary">
                  {saveStep}
                </Typography>
              </Stack>
            )}

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={onClose} color="inherit" sx={{ color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 120 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Crear Persona'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── Edit Person Dialog ────────────────────────────────────────────────────

export function EditPersonDialog({
  person,
  onClose,
  tenantId,
  institutionalOnly = false,
}: {
  person: PersonData;
  onClose: () => void;
  tenantId: string | null;
  institutionalOnly?: boolean;
}) {
  const { t } = useTranslation('sis');
  const { isPlatformAdmin } = useAuth();

  // Derive initial lifecycle stage from types[]
  const LIFECYCLE_STAGES = ['externo', 'prospecto-académico', 'student', 'alumni'];
  const initialLifecycle = person.types?.find((t) => LIFECYCLE_STAGES.includes(t)) ?? '';

  // institutionalRoles: prefer new field, fall back to institutional types in types[]
  const INST_VALUES = INSTITUTIONAL_ROLES.map((r) => r.value);
  const initialInstitutional: string[] = person.institutionalRoles?.length
    ? person.institutionalRoles
    : (person.types?.filter((t) => INST_VALUES.includes(t)) ?? []);

  const [form, setForm] = useState({
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email,
    organizationName: person.organizationName || '',
    lifecycleStage: initialLifecycle,
    institutionalRoles: initialInstitutional,
    tags: person.tags || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantId) {
      setError('Error interno: No se ha seleccionado una organización.');
      return;
    }

    const types: string[] = [];
    if (form.lifecycleStage) types.push(form.lifecycleStage);

    setSaving(true);
    setError('');
    try {
      const updatePerson = callable('updatePerson');
      await updatePerson({
        tenantId,
        personId: person.id,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        organizationName: form.organizationName,
        types,
        institutionalRoles: form.institutionalRoles,
        tags: form.tags,
      });
      onClose();
    } catch (err: unknown) {
      setError(errorText(err, t('people.dialogs.updateError')));
    } finally {
      setSaving(false);
    }
  }

  function toggleInstitutional(role: string) {
    setForm((f) => ({
      ...f,
      institutionalRoles: f.institutionalRoles.includes(role)
        ? f.institutionalRoles.filter((r) => r !== role)
        : [...f.institutionalRoles, role],
    }));
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EditOutlined />
          Editar Persona
        </Stack>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Nombre"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                required
                fullWidth
              />
              <TextField
                label="Apellido"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                required
                fullWidth
              />
            </Stack>
            <TextField
              label="Correo electrónico"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label="Organización / Iglesia"
              value={form.organizationName}
              onChange={(e) => setForm((f) => ({ ...f, organizationName: e.target.value }))}
              fullWidth
            />

            <Divider />

            {/* Ciclo de vida */}
            {!institutionalOnly && (
              <Box>
                <SectionHeader
                  icon={<SchoolRounded fontSize="small" />}
                  title="Ciclo académico"
                  subtitle="¿En qué etapa está esta persona?"
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                  {LIFECYCLE_OPTIONS.map((opt) => (
                    <RoleToggleChip
                      key={opt.value}
                      label={t(`people.types.${opt.value}`, { defaultValue: opt.label })}
                      selected={form.lifecycleStage === opt.value}
                      onToggle={() =>
                        setForm((f) => ({
                          ...f,
                          lifecycleStage: f.lifecycleStage === opt.value ? '' : opt.value,
                        }))
                      }
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Cargo institucional */}
            <Box>
              <SectionHeader
                icon={<GroupsRounded fontSize="small" />}
                title="Cargo institucional"
                subtitle="¿Qué función desempeña en la institución?"
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
                {INSTITUTIONAL_ROLES.map((role) => (
                  <RoleToggleChip
                    key={role.value}
                    label={t(`people.types.${role.value}`, { defaultValue: role.label })}
                    description={role.description}
                    selected={form.institutionalRoles.includes(role.value)}
                    onToggle={() => toggleInstitutional(role.value)}
                    color="success"
                  />
                ))}
              </Box>
            </Box>

            <Divider />

            <Autocomplete
              multiple
              freeSolo
              // Lo escrito se agrega también al SALIR del campo, no solo con Enter:
              // antes, guardar sin presionar Enter perdía la etiqueta sin aviso.
              autoSelect
              options={[]}
              value={form.tags}
              // La marca de fallecido es un dato del origen: solo un platformAdmin
              // la agrega o la quita (el servidor lo exige igual). Para los demás
              // queda como estaba, escriban lo que escriban.
              onChange={(_, newValue) =>
                setForm((f) => ({
                  ...f,
                  tags: isPlatformAdmin ? newValue : keepSourceTags(person.tags ?? [], newValue),
                }))
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const { key, onDelete, ...tagProps } = getTagProps({ index });
                  if (option !== DECEASED_IN_SOURCE_TAG) {
                    return (
                      <Chip
                        variant="outlined"
                        label={option}
                        key={key}
                        onDelete={onDelete}
                        {...tagProps}
                      />
                    );
                  }
                  // Traducida, nunca el código crudo; y bloqueada si no es
                  // platformAdmin.
                  return (
                    <Tooltip
                      key={key}
                      title={isPlatformAdmin ? '' : t('academicRecord.deceasedTagLocked')}
                    >
                      <Chip
                        variant="outlined"
                        label={t('academicRecord.deceasedTag')}
                        {...(isPlatformAdmin
                          ? { onDelete }
                          : { icon: <LockOutlined fontSize="small" /> })}
                        {...tagProps}
                      />
                    </Tooltip>
                  );
                })
              }
              renderInput={({ size: _size, ...params }) => (
                <TextField
                  {...params}
                  InputLabelProps={params.InputLabelProps as any}
                  label={t('people.dialogs.tags')}
                  placeholder={t('people.dialogs.tagsPlaceholder')}
                  helperText={t('people.dialogs.tagsHelp')}
                />
              )}
            />

            {error && (
              <Typography color="error" variant="body2">
                {error}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={onClose} color="inherit" sx={{ color: 'text.secondary' }}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={saving} sx={{ minWidth: 120 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Guardar Cambios'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

// ─── Delete Person Dialog ──────────────────────────────────────────────────

export function DeletePersonDialog({
  person,
  onClose,
  tenantId,
}: {
  person: PersonData;
  onClose: () => void;
  tenantId: string | null;
}) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  async function handleDelete() {
    if (!tenantId) {
      setError('Error interno: No se ha seleccionado una organización.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const deletePersonFn = callable('softDeletePerson');
      await deletePersonFn({ tenantId, personId: person.id });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Ocurrió un error al eliminar a la persona');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ color: 'error.main' }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <DeleteOutline />
          Eliminar Persona
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography>
          ¿Estás seguro que deseas eliminar a{' '}
          <strong>
            {person.firstName} {person.lastName}
          </strong>
          ? Esta acción no se puede deshacer de forma sencilla.
        </Typography>
        {error && (
          <Typography color="error" variant="body2" mt={2}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 0 }}>
        <Button onClick={onClose} color="inherit" sx={{ color: 'text.secondary' }}>
          Cancelar
        </Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
          {deleting ? <CircularProgress size={20} color="inherit" /> : 'Sí, eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
